import type { IProductBundleOption } from "@rogue/universal"
import type { BundleUIOption, BundleEvaluationContext, UnitCollection, MonsterMassStorageRule } from "../RuleTypes"
import type { Product, IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { findBundleProduct } from "@/js/pages/product/rackbuilder/utils/rackbuilder-util"

export function getOptionsForMassStorage(rogue: IRogue, rack: Product, bundleOption: IProductBundleOption, options: BundleUIOption[], bundleBuyRequest: BuyRequest, isMonsterMassStorage = false): BundleUIOption[] | false {
  const title = bundleOption.title.toLowerCase().replace(':', '').trim()
  const unitHeight = rack.attributes.mass_height?.toString().replace('"', '') || ''
  const unitWidth = rack.attributes.mass_width?.toString().replace('"', '') || ''

  // Rule functions for filtering or hiding MMS bundle options
  function hideShelf4And5For43Rack(currentOptions: BundleEvaluationContext) {
    if (currentOptions.is43 && (currentOptions.isShelf4 || currentOptions.isShelf5)) {
      return false // Returning `false` hides the option group and stops further rule evaluation
    }

    return undefined // Returning `undefined` means no action (rule does not apply)
  }

  function hideShelf5For70Rack(currentOptions: BundleEvaluationContext) {
    if (currentOptions.is70 && currentOptions.isShelf5) {
      return false
    }

    return undefined
  }

  function filterOptionsByUnitWidth(currentOptions: BundleEvaluationContext) {
    if (currentOptions.unitWidth === '70') {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('43'))
    }

    if (currentOptions.unitWidth === '43') {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('70"'))
    }

    return undefined
  }

  function filterCornerOptionsByHeight(currentOptions: BundleEvaluationContext) {
    if (currentOptions.is43 && !currentOptions.title.includes('wheels')) {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('3 pack')
        && !opt.label.toLowerCase().includes('4 pack'))
    }
    if (currentOptions.unitHeight === '70' && !currentOptions.title.includes('wheels')) {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('4 pack'))
    }

    return undefined
  }

  // Helper function to check if current unit is next to a 100" unit
  function isUnitNextTo100(currentOptions: BundleEvaluationContext): boolean {
    const currentUnitId = currentOptions.bundleBuyRequest?.metadata?.id
    const unitCollection: UnitCollection[] = currentOptions.bundleBuyRequest?.metadata?.unitCollection

    // Find the current unit in the collection
    const currentUnitIndex = unitCollection?.findIndex(unit => unit.id === currentUnitId)

    if (currentUnitIndex === -1) {
      return false // Current unit not found in collection
    }

    // Check if there's a 100" unit adjacent to the current unit
    return unitCollection?.some((unit, index) => {
    // Skip the current unit itself
      if (unit.id === currentUnitId) return false

      // Check if this unit is 100" and adjacent (next to each other in the array)
      const isAdjacent = Math.abs(index - currentUnitIndex) === 1
      const is100Height = unit.height === '100'

      return isAdjacent && is100Height
    })
  }

  // Hide third shelf option on a 43" unit when next to a 100" unit
  function hideThirdShelfWhenNextTo100(currentOptions: BundleEvaluationContext) {
  // Only apply this rule to 43" units on shelf 3
    if (!currentOptions.is43 || !currentOptions.isShelf3) {
      return undefined
    }

    // If current 43" unit is next to a 100" unit, hide shelf 3
    if (isUnitNextTo100(currentOptions)) {
      return false
    }

    return undefined
  }

  // Hide fourth shelf option on a 70" unit when next to a 100" unit and parallel shelves are selected
  function modifyShelf4WhenNextTo100(currentOptions: BundleEvaluationContext) {
  // Only apply this rule to 70" units
    if (!currentOptions.is70) {
      return undefined
    }

    const isNextTo100Unit = isUnitNextTo100(currentOptions)

    if (isNextTo100Unit) {
    // Check for parallel shelf selections
      const shelfOneSelection = findBundleProduct(currentOptions.rogue, currentOptions.bundleBuyRequest, 'shelf 1', true)?.name
      const shelfTwoSelection = findBundleProduct(currentOptions.rogue, currentOptions.bundleBuyRequest, 'shelf 2', true)?.name
      const shelfThreeSelection = findBundleProduct(currentOptions.rogue, currentOptions.bundleBuyRequest, 'shelf 3', true)?.name

      const isParallelIn1 = shelfOneSelection?.toLowerCase().includes('parallel')
      const isParallelIn2 = shelfTwoSelection?.toLowerCase().includes('parallel')
      const isParallelIn3 = shelfThreeSelection?.toLowerCase().includes('parallel')

      const parallelCount = [isParallelIn1, isParallelIn2, isParallelIn3].filter(Boolean).length

      // If at least 2 parallel shelves are selected, hide the shelf 4 option
      if (parallelCount >= 2 && currentOptions.isShelf4) {
        return false
      }

      // Hide dumbbell shelves if next to 100 and previous condition did not apply
      if (currentOptions.isShelf4) {
        return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('dumbbell'))
      }
    }

    return undefined
  }

  function hideIncompatibleCornerShelfTypes(currentOptions: BundleEvaluationContext) {
    const isCorner = currentOptions.unitWidth.toLowerCase() === 'corner'
    const isAskingForCornerShelves = currentOptions.title.includes('add corner shelf')
    const isGeneralShelf = currentOptions.title.includes('shelf') && !currentOptions.title.includes('corner shelf')

    if (!isCorner && isAskingForCornerShelves) {
      return false
    }

    if (!isCorner) {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('corner'))
    }

    if (isCorner && isGeneralShelf) {
      return false
    }

    return undefined
  }

  function filterNoneOptionOnFirstShelves(currentOptions: BundleEvaluationContext) {
    if (currentOptions.isShelf1 || currentOptions.isShelf2) {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('none'))
    }

    return undefined
  }

  function filterNoneOptionForCornerShelves(currentOptions: BundleEvaluationContext) {
    if (currentOptions.bundleOption.title.toLowerCase().includes('add corner shelf')) {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('none'))
    }

    return undefined
  }

  // Hide the dumbbell option on shelf 3 for 43" units — MMS only, MS now allows it
  function hideDumbbellShelfOnShelf3(currentOptions: BundleEvaluationContext) {
    if (currentOptions.isShelf3 && currentOptions.is43) {
      return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('dumbbell'))
    }

    return undefined
  }

  // Hide DB shelves on option 4 only on 70" units if any shelves have parallel selected
  function hideDumbbellShelfOnShelf4(currentOptions: BundleEvaluationContext) {
    if (currentOptions.is70 && currentOptions.isShelf4) {
      const shelfOneSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 1', true)?.name
      const shelfTwoSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 2', true)?.name
      const shelfThreeSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 3', true)?.name

      const isParallelIn1 = shelfOneSelection?.toLowerCase().includes('parallel')
      const isParallelIn2 = shelfTwoSelection?.toLowerCase().includes('parallel')
      const isParallelIn3 = shelfThreeSelection?.toLowerCase().includes('parallel')

      if (isParallelIn1 || isParallelIn2 || isParallelIn3) {
        return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('dumbbell'))
      }
    }

    return undefined
  }

  // Hide DB shelves on option 5 for 100" shelves if shelf all shelves have parallel selected
  function hideDumbbellShelfOnShelf5(currentOptions: BundleEvaluationContext) {
    if (currentOptions.is100 && currentOptions.isShelf5) {
      const shelfOneSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 1', true)?.name
      const shelfTwoSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 2', true)?.name
      const shelfThreeSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 3', true)?.name
      const shelfFourSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 4', true)?.name

      const isParallelIn1 = shelfOneSelection?.toLowerCase().includes('parallel')
      const isParallelIn2 = shelfTwoSelection?.toLowerCase().includes('parallel')
      const isParallelIn3 = shelfThreeSelection?.toLowerCase().includes('parallel')
      const isParallelIn4 = shelfFourSelection?.toLowerCase().includes('parallel')

      if (isParallelIn1 || isParallelIn2 || isParallelIn3 || isParallelIn4) {
        return currentOptions.options.filter(opt => !opt.label.toLowerCase().includes('dumbbell'))
      }
    }

    return undefined
  }

  // Hide the crossmember/rail option on shelf 3 for 43" units when shelf 2 is a dumbbell shelf
  function hideRailShelfOnShelf3WhenShelf2Dumbbell(currentOptions: BundleEvaluationContext) {
    if (currentOptions.isShelf3 && currentOptions.is43) {
      const shelfTwoSelection = findBundleProduct(rogue, bundleBuyRequest, 'shelf 2', true)?.name
      const isDumbbellIn2 = shelfTwoSelection?.toLowerCase().includes('dumbbell')

      if (isDumbbellIn2) {
        return currentOptions.options.filter(opt => {
          const label = opt.label.toLowerCase()

          return !label.includes('crossmember') && label !== 'rail'
        })
      }
    }

    return undefined
  }

  const monsterMassStorageRules: MonsterMassStorageRule[] = [
    hideShelf4And5For43Rack,
    hideShelf5For70Rack,
    hideThirdShelfWhenNextTo100,
    modifyShelf4WhenNextTo100,
    filterOptionsByUnitWidth,
    filterCornerOptionsByHeight,
    hideIncompatibleCornerShelfTypes,
    filterNoneOptionOnFirstShelves,
    filterNoneOptionForCornerShelves,
    hideRailShelfOnShelf3WhenShelf2Dumbbell,
    // MS now allows dumbbell on shelf 3 for 43" units — MMS still restricts it, along with shelf 4's different logic
    ...(isMonsterMassStorage ? [hideDumbbellShelfOnShelf3, hideDumbbellShelfOnShelf4] : []),
    hideDumbbellShelfOnShelf5,
  ]

  // Start with a copy of the original options array that will be filtered by rule functions
  let updatedOptions: BundleUIOption[] = [...options]
  let shouldStop = false

  // Evaluation context passed to each rule, including rack details, product metadata, and the current filtered options
  const currentOptionsState: BundleEvaluationContext = {
    rogue,
    rack,
    bundleOption,
    options  : updatedOptions,
    bundleBuyRequest,
    title,
    unitHeight,
    unitWidth,
    is43     : unitHeight === '43',
    is70     : unitHeight === '70',
    is100    : unitHeight === '100',
    isCorner : unitWidth.toLowerCase() === 'corner',
    isShelf1 : title.includes('shelf 1'),
    isShelf2 : title.includes('shelf 2'),
    isShelf3 : title.includes('shelf 3'),
    isShelf4 : title.includes('shelf 4'),
    isShelf5 : title.includes('shelf 5'),
  }

  // Iterate over each function in the array
  monsterMassStorageRules.forEach(rule => {
    if (shouldStop) return

    const result = rule(currentOptionsState)

    if (result === false) {
      updatedOptions = false as any // Stores the signal that this option group should be hidden entirely
      shouldStop = true

      return
    }

    // If the rule returns a new filtered options array, update the current state
    if (Array.isArray(result)) {
      updatedOptions = result
      currentOptionsState.options = updatedOptions
    }
  })

  return updatedOptions
}